import { ReportSection } from "~/types/global";
import { REPORT_CONSTANTS, REPORT_SECTIONS } from "~/helpers/constants";

export function extractCombinadoSections(
  sections: ReportSection[],
  selectedFilter: string
): ReportSection[] {
  const combinadoContainer = sections.find(
    (section: any) => section.combined_data || section.report_sources_data
  );

  if (!combinadoContainer) return sections;

  const showCombinedData = selectedFilter === REPORT_CONSTANTS.combined_data_keys.combined_data;

  if (showCombinedData && Array.isArray((combinadoContainer as any).combined_data)) {
    return (combinadoContainer as any).combined_data;
  } else if (Array.isArray((combinadoContainer as any).report_sources_data)) {
    // Extract report ID from composite value "reportId__index"
    const reportId = selectedFilter.split('__')[0];

    return (combinadoContainer as any).report_sources_data.filter(
      (sourceSection: any) =>
        !!sourceSection && sourceSection.user_reports_id === reportId
    );
  }

  return sections;
}

export function findMatchingSections(
  sections: ReportSection[],
  sectionTitle: string,
  reportType: string,
  selectedCombinedFilter: string,
  includeSubsections: boolean = false
): ReportSection[] {
  let sectionsToSearch: ReportSection[] = [];

  if (reportType === REPORT_CONSTANTS.types.combinado) {
    sectionsToSearch = extractCombinadoSections(sections, selectedCombinedFilter);
  } else {
    sectionsToSearch = sections;
  }

  return sectionsToSearch.filter((s) => {
    const titleMatches = s.title === sectionTitle;
    const subsectionMatches = includeSubsections || !s.subsection;
    return titleMatches && subsectionMatches;
  });
}

export function findSection(
  sections: ReportSection[],
  sectionTitle: string,
  reportType: string,
  selectedCombinedFilter: string,
  subsectionName?: string
): ReportSection | null {
  const sectionsToSearch = reportType === REPORT_CONSTANTS.types.combinado
    ? extractCombinadoSections(sections, selectedCombinedFilter)
    : sections;

  return sectionsToSearch.find((s) => {
    const titleMatches = s.title === sectionTitle;
    const subsectionMatches = subsectionName
      ? s.subsection === subsectionName
      : !s.subsection;
    return titleMatches && subsectionMatches;
  }) || null;
}

export function calculateSectionListRecords(
  sections: ReportSection[],
  reportType: string,
  selectedCombinedFilter: string
) {
  const sectionsToProcess = reportType === REPORT_CONSTANTS.types.combinado
    ? extractCombinadoSections(sections, selectedCombinedFilter)
    : sections;

  const sectionListRecords = sectionsToProcess
    .filter((section: any) => {
      // `subsection` marks a grouping variant that should stay hidden from the
      // top-level list only for `Processos` (matches `filterValidSections`).
      // `redes_sociais` sections always carry a `subsection` (the network name,
      // e.g. "instagram"), so excluding every `subsection` here would drop
      // them from `sectionListRecords` and, via `cleanOpenSections`, close
      // their accordion right after any delete/restore action.
      // Suffix match (not ===) so veiculo's embedded-owner titles ("Proprietário
      // (CPF) — Processos"/"Empresa (CNPJ) — Processos") are excluded too, same
      // as filterValidSections — otherwise each vinculo bucket's raw section
      // listed itself in the índice as a separate duplicate entry. Found live 2026-09-09.
      if (section.title.endsWith(REPORT_SECTIONS.processos) && section.subsection) return false;
      if (section.is_deleted) return false;
      if (section.data_count > 0) return true;

      return (
        Array.isArray(section.data) &&
        section.data.some(
          (item: any) =>
            (Array.isArray(item.detalhes) && item.detalhes.length > 0) ||
            (item.detalhes && typeof item.detalhes === "object" && Object.keys(item.detalhes).length > 0)
        )
      );
    })
    .map((s: any, index: number) => ({
      title: s.title,
      data_count: s.data_count,
      index,
      interface_id: s.interface_id,
      subsection: s.subsection,
    }));

  const totalRecords = sectionListRecords.reduce((sum: number, section: any) => {
    const count = Number(section.data_count) || 0;
    return sum + count;
  }, 0);

  return { sectionListRecords, totalRecords };
}

export function cleanOpenSections(
  openSections: string[],
  validSectionIds: Set<string>,
  sectionListRecords: any[]
): string[] {
  return openSections.filter((id: string) => {
    return (
      validSectionIds.has(id) ||
      sectionListRecords.some(
        (s: any) => s.title.toLowerCase().replace(/\s+/g, "-") === id
      )
    );
  });
}

export function isActiveSectionValid(
  activeSection: string | null,
  validSectionIds: Set<string>,
  sectionListRecords: any[]
): boolean {
  if (!activeSection) return false;

  return (
    validSectionIds.has(activeSection) ||
    sectionListRecords.some(
      (s: any) => s.title.toLowerCase().replace(/\s+/g, "-") === activeSection
    )
  );
}
